import { AABB, BoundingSphere, DuoMeasurementValues, View } from "@novorender/api";
import { quat, ReadonlyVec3, vec3 } from "gl-matrix";
import { type MutableRefObject, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector, useAppStore } from "app/redux-store-interactions";
import type { AppDispatch } from "app/store";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import {
    HighlightCollection,
    highlightCollectionsActions,
    useDispatchHighlightCollections,
} from "contexts/highlightCollections";
import {
    type CameraState,
    CameraType,
    ObjectVisibility,
    Picker,
    renderActions,
    selectDefaultVisibility,
    selectPicker,
} from "features/render";
import { suppressNextCameraHistoryRecording } from "features/render/hooks/cameraHistorySuppression";
import { undoRedoActions } from "features/undoRedo";
import useFlyTo from "hooks/useFlyTo";
import { explorerActions } from "slices/explorer";
import { telemetry } from "telemetry";
import { isDebugEnabled, log } from "utils/log";

import {
    measureActions,
    type ObjectMeasureState,
    selectObjectMeasure,
    selectObjectMeasureIds,
    selectObjectMeasureResult,
} from "./measureSlice";

const l = log.measure.objectToObject;

/**
 * The search gets slower the further apart the objects are, so try a capped search first and only
 * fall back to an uncapped one if it finds nothing. The user picked these two objects deliberately,
 * so a distance beyond the cap is still a valid answer - it just costs more to find.
 */
const MAX_DISTANCE_METERS = 100;

/** Stable empty array, so that clearing the highlight does not re-fire the effect every render. */
const NO_IDS: number[] = [];

/**
 * Drives {@link Picker.ObjectMeasurement}: once two objects are picked, measures the shortest line
 * between them and highlights the picks. The result survives leaving the tool - like the other
 * measure tools, it is kept on save and only reverted when the user cancels out of the mode, which
 * the edit-session snapshot in `measureSlice` takes care of.
 *
 * A finished measurement is undoable as a whole - see {@link commitMeasurement}. The individual
 * picks leading up to it are not, since a half-picked pair is not a state worth stepping back into:
 * picking the same object again unpicks it.
 */
export function useObjectToObjectMeasure() {
    const {
        state: { view },
    } = useExplorerGlobals();
    const dispatch = useAppDispatch();
    const store = useAppStore();
    const dispatchHighlightCollections = useDispatchHighlightCollections();
    const flyTo = useFlyTo();
    const picker = useAppSelector(selectPicker);
    const objectIds = useAppSelector(selectObjectMeasureIds);
    const { t } = useTranslation();

    useHighlightMeasuredObjects();

    // Picking the objects for a new measurement drops the one on screen without going through the
    // undo stack, so whatever is on screen is remembered here for the next measurement's undo to
    // step back to. Tracked off the state rather than written when we measure, so that it also
    // covers measurements this hook did not make itself - one restored with the scene, or brought
    // back by cancelling a session. Emptying the picks - clearing the measurement outright, or
    // cancelling out of one - ends the run: there is then nothing left for an undo to bring back.
    const replacedMeasurement = useRef<ObjectMeasureState | undefined>(undefined);
    const objectMeasure = useAppSelector(selectObjectMeasure);
    useEffect(() => {
        if (objectMeasure.result) {
            replacedMeasurement.current = objectMeasure;
        } else if (objectMeasure.objectIds.length === 0) {
            replacedMeasurement.current = undefined;
        }
    }, [objectMeasure]);

    // Tinting a pick is an affordance for the picking itself, so it stops with the tool, and stops
    // once the pair is complete - from then on the two objects are shown in their own right, by
    // `useHighlightMeasuredObjects`.
    const highlightIds = picker === Picker.ObjectMeasurement && objectIds.length < 2 ? objectIds : NO_IDS;

    // The secondary highlight is shared with other features, so only clear it if we put something
    // there ourselves - otherwise mounting this hook would wipe e.g. search result highlighting.
    const ownsHighlight = useRef(false);
    useEffect(() => {
        if (highlightIds.length > 0) {
            ownsHighlight.current = true;
        } else if (!ownsHighlight.current) {
            return;
        } else {
            ownsHighlight.current = false;
        }

        dispatchHighlightCollections(
            highlightCollectionsActions.setIds(HighlightCollection.SecondaryHighlight, highlightIds),
        );
    }, [highlightIds, dispatchHighlightCollections]);

    useEffect(() => {
        if (objectIds.length < 2) {
            return;
        }

        let aborted = false;
        measure();
        return () => {
            aborted = true;
        };

        async function measure() {
            const [a, b] = objectIds;
            const measureView = view?.measure;
            if (!measureView || aborted) {
                return;
            }

            // Redoing a measurement puts the finished result back into state together with its pair
            // of ids, which re-runs this effect. There is nothing left to measure in that case.
            if (selectObjectMeasureResult(store.getState())) {
                return;
            }

            dispatch(measureActions.setObjectMeasureLoading(true));
            try {
                const start = performance.now();
                let result = await measureView.core.measureObjectToObject(a, b, {
                    maxDistance: MAX_DISTANCE_METERS,
                });
                l.debug(
                    `Measured ${a} to ${b} within ${MAX_DISTANCE_METERS}m in ${Math.round(
                        performance.now() - start,
                    )}ms:`,
                    result,
                );

                if (!result) {
                    const uncappedStart = performance.now();
                    result = await measureView.core.measureObjectToObject(a, b);
                    l.debug(
                        `Retried ${a} to ${b} uncapped in ${Math.round(performance.now() - uncappedStart)}ms:`,
                        result,
                    );
                }

                await logPickedObjectExtents(view, a, b, result);

                if (aborted) {
                    return;
                }

                if (result) {
                    commitMeasurement({
                        dispatch,
                        view,
                        flyTo,
                        objectIds: [a, b],
                        result,
                        visibilityBefore: selectDefaultVisibility(store.getState()),
                        replacedMeasurement,
                    });
                } else {
                    dispatch(measureActions.setObjectMeasureResult(undefined));

                    // Both objects need parametric data. Report that separately, because it means
                    // these objects can never be measured rather than that nothing was found.
                    const [productA, productB] = await Promise.all([
                        measureView.core.getParametricProduct(a).catch(() => undefined),
                        measureView.core.getParametricProduct(b).catch(() => undefined),
                    ]);
                    l.warn(
                        `No connection between ${a} and ${b}. Parametric data:`,
                        `${a}=${Boolean(productA)}`,
                        `${b}=${Boolean(productB)}`,
                    );

                    if (aborted) {
                        return;
                    }

                    dispatch(
                        explorerActions.setSnackbarMessage({
                            msg: productA && productB ? t("objectMeasureNoResult") : t("objectMeasureNoParametricData"),
                            closeAfter: 5000,
                        }),
                    );
                }
            } catch (e) {
                l.error(`Failed to measure ${a} to ${b}:`, e);
                console.warn(e);
                telemetry.trackCaughtException({
                    source: "measure.useObjectToObjectMeasure",
                    category: "render",
                    error: e,
                });
                if (!aborted) {
                    dispatch(measureActions.setObjectMeasureResult(undefined));
                    dispatch(explorerActions.setSnackbarMessage({ msg: t("objectMeasureNoResult"), closeAfter: 5000 }));
                }
            }
        }
    }, [view, objectIds, dispatch, store, flyTo, t]);
}

/**
 * Applies a finished measurement and pushes it as a single undoable action.
 *
 * The result, the x-ray mode that makes it stand out, and the camera move that frames it all land
 * together as one thing the user did, so they are undone together too: stepping back through them
 * one at a time would leave a measurement buried in the model, or a camera framing nothing.
 *
 * Undo goes back to the measurement this one replaced, if there was one, so a run of measurements
 * can be stepped back through in order. The picks are never restored on their own - a pair left
 * selected would only make the measuring effect measure it again.
 */
function commitMeasurement({
    dispatch,
    view,
    flyTo,
    objectIds,
    result,
    visibilityBefore,
    replacedMeasurement,
}: {
    dispatch: AppDispatch;
    view: View | undefined;
    flyTo: ReturnType<typeof useFlyTo>;
    objectIds: number[];
    result: DuoMeasurementValues;
    visibilityBefore: ObjectVisibility;
    replacedMeasurement: MutableRefObject<ObjectMeasureState | undefined>;
}) {
    const measured: ObjectMeasureState = { objectIds, result, loading: false, noResult: false };
    const replaced = replacedMeasurement.current;
    const cameraBefore = snapshotCamera(view);
    const sphere = fitSphere(result, view);

    const apply = () => {
        dispatch(measureActions.setObjectMeasure(measured));
        dispatch(renderActions.setVisibilityMode(ObjectVisibility.Xray));
        if (sphere) {
            // The camera move belongs to this action, so keep the camera-moved handler from
            // recording a second entry of its own for it.
            suppressNextCameraHistoryRecording();
            void flyTo({ sphere });
        }
    };

    apply();

    dispatch(
        undoRedoActions.push({
            description: "Measure object to object",
            undoCallback: () => {
                dispatch(replaced ? measureActions.setObjectMeasure(replaced) : measureActions.clearObjectMeasure());
                dispatch(renderActions.setVisibilityMode(visibilityBefore));
                if (cameraBefore) {
                    suppressNextCameraHistoryRecording();
                    dispatch(renderActions.setCamera(cameraBefore));
                }
            },
            redoCallback: apply,
        }),
    );
}

/** Where the camera was before a measurement moved it, in the shape needed to put it back. */
function snapshotCamera(view: View | undefined): CameraState | undefined {
    const camera = view?.renderState.camera;
    if (!camera) {
        return undefined;
    }

    const goTo = {
        position: vec3.clone(camera.position),
        rotation: quat.clone(camera.rotation),
        fov: camera.fov,
    };
    return camera.kind === "orthographic"
        ? { type: CameraType.Orthographic, goTo: { ...goTo, far: camera.far } }
        : { type: CameraType.Pinhole, goTo };
}

/**
 * Once both objects are picked, puts the pair into a highlight collection that renders them with
 * their default appearance, so the thing being measured stands out of the x-rayed geometry around
 * it. The x-ray mode itself is switched on by {@link commitMeasurement} and back off on the way out
 * of the tool - see `objectMeasureXray`.
 */
function useHighlightMeasuredObjects() {
    const dispatchHighlightCollections = useDispatchHighlightCollections();
    const objectIds = useAppSelector(selectObjectMeasureIds);

    const measuredIds = objectIds.length === 2 ? objectIds : NO_IDS;
    const ownsCollection = useRef(false);

    useEffect(() => {
        if (measuredIds.length > 0) {
            ownsCollection.current = true;
        } else if (!ownsCollection.current) {
            return;
        } else {
            ownsCollection.current = false;
        }

        dispatchHighlightCollections(
            highlightCollectionsActions.setIds(HighlightCollection.ObjectMeasure, measuredIds),
        );
    }, [measuredIds, dispatchHighlightCollections]);
}

/** Keeps the measured line off the edges of the viewport instead of spanning it exactly. */
const FIT_PADDING = 1.4;

/**
 * Touching objects measure to nearly nothing, and fitting that would put the camera inside the
 * geometry, so never frame anything tighter than this, in meters.
 */
const MIN_FIT_RADIUS_METERS = 1;

/**
 * The sphere that frames a finished measurement: centred on the measured line and wide enough to
 * fit it in the frustum, so flying to it keeps the camera's orientation and just pulls back far
 * enough to show the whole thing. Undefined when the result has no line to frame.
 */
function fitSphere(result: DuoMeasurementValues, view: View | undefined): BoundingSphere | undefined {
    const a = result.measureInfoA?.point;
    const b = result.measureInfoB?.point;
    if (!a || !b || !view) {
        return undefined;
    }

    const radius = Math.max((vec3.distance(a, b) / 2) * FIT_PADDING, MIN_FIT_RADIUS_METERS);
    // Both camera types fit the sphere against the vertical field of view, so a portrait viewport
    // needs a larger sphere to keep a horizontal line from running off the sides.
    const { width, height } = view.renderState.output;
    const aspect = width > 0 && height > 0 ? width / height : 1;

    return {
        center: vec3.lerp(vec3.create(), a, b, 0.5),
        radius: aspect < 1 ? radius / aspect : radius,
    };
}

/** Slack when testing a measured point against a bounding box, in meters. */
const BOUNDS_EPSILON = 0.01;

function isInsideBounds(point: ReadonlyVec3, box: AABB) {
    for (let i = 0; i < 3; ++i) {
        if (point[i] < box.min[i] - BOUNDS_EPSILON || point[i] > box.max[i] + BOUNDS_EPSILON) {
            return false;
        }
    }
    return true;
}

/**
 * Logs what was actually picked and where the measured points landed, to tell a mis-pick apart from
 * a bad measurement: a closest point outside its own object's bounding box can only be the latter.
 * Only runs when the log namespace is enabled, since it costs a metadata lookup per object.
 */
async function logPickedObjectExtents(
    view: View | undefined,
    a: number,
    b: number,
    result: DuoMeasurementValues | undefined,
) {
    if (!view?.data || !isDebugEnabled(l.namespace)) {
        return;
    }

    const objects = await Promise.all(
        [
            { id: a, point: result?.measureInfoA?.point },
            { id: b, point: result?.measureInfoB?.point },
        ].map(async ({ id, point }) => {
            try {
                const { name, path, bounds } = await view.data!.getObjectMetaData(id);
                return { id, name, path, box: bounds?.box, point };
            } catch (e) {
                l.warn(`Could not read metadata for ${id}:`, e);
                return { id, point };
            }
        }),
    );

    l.debug("Picked objects:", objects);

    for (const { id, box, point } of objects) {
        if (box && point && !isInsideBounds(point, box)) {
            l.warn(
                `Measured point on ${id} is outside the object's own bounds - the measurement is wrong, not the pick.`,
                { point, box },
            );
        }
    }
}
